import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {'off' | 'hover' | 'autoplay'} CardVideoMode
 */

const touchQuery = matchMedia('(hover: none)');
const reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)');

// How many cards may autoplay at once. Keeps a wide desktop grid from decoding a whole row of videos.
// Touch works in whole rows so side-by-side video cards never split: the most visible row always plays, and
// further rows only join while the total stays within the touch cap (one row of the two-column mobile grid).
const MAX_AUTOPLAYING_DESKTOP = 4;
const MAX_AUTOPLAYING_TOUCH = 2;

// Cards whose tops are within this many pixels of each other are treated as the same grid row.
const ROW_TOLERANCE = 8;

// Share a single observer and registry across every card. Cards are re-rendered constantly by swatches,
// filtering and pagination, so per-instance observers would leak.

/** @type {Set<CardVideo>} */
const instances = new Set();

/** @type {Map<CardVideo, number>} Autoplay cards that are at least half in view, with their visible ratio */
const inView = new Map();

/** @type {Set<CardVideo>} */
const autoplaying = new Set();

const observer = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const cardVideo = /** @type {CardVideo} */ (entry.target);

      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        inView.set(cardVideo, entry.intersectionRatio);
      } else {
        inView.delete(cardVideo);
      }
    }

    reconcileAutoplay();
  },
  { threshold: [0, 0.5, 0.75, 1] }
);

/**
 * Whether the shopper's device or preferences allow card videos at all.
 * @returns {boolean}
 */
function canPlay() {
  const connection = /** @type {{ saveData?: boolean } | undefined} */ (
    /** @type {any} */ (navigator).connection
  );

  return !prefersReducedMotion() && connection?.saveData !== true;
}

/**
 * Picks which in-view autoplay cards should be playing, within the concurrency cap, and starts or stops them.
 */
function reconcileAutoplay() {
  const candidates = [...inView.keys()].filter((cardVideo) => cardVideo.canAutoplay);
  const selected = new Set(touchQuery.matches ? pickTouchRows(candidates) : pickDesktop(candidates));

  for (const cardVideo of autoplaying) {
    if (!selected.has(cardVideo)) {
      autoplaying.delete(cardVideo);
      cardVideo.pause();
    }
  }

  for (const cardVideo of selected) {
    if (!autoplaying.has(cardVideo)) {
      autoplaying.add(cardVideo);
      cardVideo.play();
    }
  }
}

/**
 * Desktop: cards that are already playing keep their slot, so additional cards stay on their poster rather
 * than displacing them.
 * @param {CardVideo[]} candidates
 * @returns {CardVideo[]}
 */
function pickDesktop(candidates) {
  return candidates
    .sort(
      (a, b) =>
        Number(autoplaying.has(b)) - Number(autoplaying.has(a)) || (inView.get(b) ?? 0) - (inView.get(a) ?? 0)
    )
    .slice(0, MAX_AUTOPLAYING_DESKTOP);
}

/**
 * Touch: groups cards into grid rows so a row plays or pauses together. The most visible row always plays
 * (up to the desktop cap, for wide tablet grids), then further rows join only if the whole row fits.
 * @param {CardVideo[]} candidates
 * @returns {CardVideo[]}
 */
function pickTouchRows(candidates) {
  /** @type {{ top: number, ratio: number, playing: boolean, members: CardVideo[] }[]} */
  const rows = [];

  for (const cardVideo of candidates) {
    const top = cardVideo.getBoundingClientRect().top;
    let row = rows.find((candidateRow) => Math.abs(candidateRow.top - top) < ROW_TOLERANCE);

    if (!row) {
      row = { top, ratio: 0, playing: false, members: [] };
      rows.push(row);
    }

    row.members.push(cardVideo);
    row.ratio = Math.max(row.ratio, inView.get(cardVideo) ?? 0);
    row.playing ||= autoplaying.has(cardVideo);
  }

  rows.sort((a, b) => b.ratio - a.ratio || Number(b.playing) - Number(a.playing) || a.top - b.top);

  const [firstRow, ...otherRows] = rows;
  if (!firstRow) return [];

  const selected = firstRow.members.slice(0, MAX_AUTOPLAYING_DESKTOP);

  for (const row of otherRows) {
    if (selected.length + row.members.length <= MAX_AUTOPLAYING_TOUCH) selected.push(...row.members);
  }

  return selected;
}

function syncAll() {
  for (const cardVideo of instances) cardVideo.sync();
  reconcileAutoplay();
}

touchQuery.addEventListener('change', syncAll);
reducedMotionQuery.addEventListener('change', syncAll);

// The browser pauses videos when the page is hidden (switching app or tab) or stored in the back-forward cache
// (tapping through to the PDP). Nothing changes in the viewport on return, so the observer never fires again
// and cards would stay frozen. Stop everything on the way out and pick again on the way back.
function stopAll() {
  for (const cardVideo of instances) cardVideo.pause();
  autoplaying.clear();
}

window.addEventListener('pagehide', stopAll);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) reconcileAutoplay();
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    stopAll();
  } else {
    reconcileAutoplay();
  }
});

/**
 * A custom element that plays a Shopify-hosted video on a product card.
 *
 * The poster image is always in the markup. The video lives in an inert template and is only cloned in the
 * first time playback is needed, so an idle card costs no network.
 *
 * Desktop and touch are resolved client-side from hover capability, not viewport width, because the same
 * cached HTML is served to every device.
 */
export class CardVideo extends Component {
  /** @type {HTMLVideoElement | null} */
  #video = null;

  /** @type {HTMLElement | null} */
  #slideshow = null;

  #isObserved = false;

  #isOnActiveSlide = true;

  /**
   * Watches for filter, sort and swatch re-renders. The morph resets this element and its slide to the server
   * HTML (no mounted video, no skipped slide) but keeps the same instance, and morph.js never calls
   * updatedCallback() on the live element, so the component has to notice the change itself.
   */
  #morphObserver = new MutationObserver((records) => {
    const isMorph = records.some((record) =>
      record.type === 'attributes'
        ? record.oldValue !== null && !(/** @type {Element} */ (record.target).hasAttribute('data-card-video-skipped'))
        : [...record.removedNodes].some((node) => node === this.#video)
    );

    if (isMorph) this.#recoverFromMorph();
  });

  /**
   * The playback mode that applies to the current device.
   * @returns {CardVideoMode}
   */
  get mode() {
    if (!canPlay()) return 'off';

    if (touchQuery.matches) {
      return this.dataset.modeMobile === 'autoplay' ? 'autoplay' : 'off';
    }

    const mode = this.dataset.modeDesktop;
    return mode === 'hover' || mode === 'autoplay' ? mode : 'off';
  }

  /**
   * Whether the video's slide is the one currently shown in the card's slideshow.
   * @returns {boolean}
   */
  get isOnActiveSlide() {
    return this.#isOnActiveSlide;
  }

  /**
   * Whether this card is allowed to take an autoplay slot right now.
   * @returns {boolean}
   */
  get canAutoplay() {
    return this.isConnected && this.#isOnActiveSlide && this.mode === 'autoplay';
  }

  connectedCallback() {
    super.connectedCallback();

    instances.add(this);

    this.#isOnActiveSlide = !this.closest('slideshow-slide')?.hidden;
    this.#slideshow = this.closest('slideshow-component');
    this.#slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.#morphObserver.observe(this, { childList: true });
    const slide = this.closest('slideshow-slide');
    if (slide) {
      this.#morphObserver.observe(slide, {
        attributes: true,
        attributeFilter: ['data-card-video-skipped'],
        attributeOldValue: true,
      });
    }

    this.sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#recoverFromMorph();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    instances.delete(this);
    this.#slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.#slideshow = null;
    this.#morphObserver.disconnect();

    observer.unobserve(this);
    this.#isObserved = false;
    inView.delete(this);
    autoplaying.delete(this);
    this.pause();

    reconcileAutoplay();
  }

  /**
   * Registers or unregisters with the viewport observer to match the current mode.
   * Only autoplay cards are observed, so hover and off cost nothing on that device type.
   */
  sync() {
    const mode = this.mode;

    if (mode === 'autoplay' && !this.#isObserved) {
      observer.observe(this);
      this.#isObserved = true;
    } else if (mode !== 'autoplay' && this.#isObserved) {
      observer.unobserve(this);
      this.#isObserved = false;
      inView.delete(this);
      autoplaying.delete(this);
    }

    if (mode === 'off') this.pause();

    this.#syncSlide(mode === 'off');
  }

  /**
   * Hides the video's slide on devices where it will never play, so the card starts on the product image,
   * using the same [hidden] slide mechanism the slideshow already applies to variant images.
   * @param {boolean} skip
   */
  #syncSlide(skip) {
    const slide = this.closest('slideshow-slide');
    if (!(slide instanceof HTMLElement) || !this.hasAttribute('data-has-fallback')) return;

    const isSkipped = slide.hasAttribute('data-card-video-skipped');

    if (skip && !isSkipped) {
      const otherSlides = [...(slide.parentElement?.children ?? [])].filter(
        (element) => element !== slide && element.matches('slideshow-slide:not([hidden])')
      );

      // Nothing else to show (e.g. every other slide is a hidden variant image), so keep the still frame
      if (!otherSlides.length) {
        this.setAttribute('data-keep-slide', '');
        return;
      }

      this.removeAttribute('data-keep-slide');
      slide.hidden = true;
      slide.setAttribute('data-card-video-skipped', '');
      slide.setAttribute('aria-hidden', 'true');
      this.#isOnActiveSlide = false;

      if (!otherSlides.some((element) => element.getAttribute('aria-hidden') === 'false')) {
        otherSlides[0]?.setAttribute('aria-hidden', 'false');
      }
    } else if (!skip && isSkipped) {
      slide.hidden = false;
      slide.removeAttribute('data-card-video-skipped');
      this.#isOnActiveSlide = true;
    }
  }

  /**
   * Drops a video that a re-render removed from the element, so the next play() clones a fresh one instead of
   * playing the detached element off screen.
   * @returns {boolean} Whether a stale video was dropped
   */
  #dropStaleVideo() {
    if (!this.#video || this.#video.isConnected) return false;

    this.#video.pause();
    this.#video = null;
    this.removeAttribute('data-playing');
    this.removeAttribute('data-video-mounted');
    autoplaying.delete(this);

    return true;
  }

  #recoverFromMorph() {
    this.#dropStaleVideo();

    // The morph also clears the skipped state off the slide, so re-apply it where playback is off
    this.#isOnActiveSlide = !this.closest('slideshow-slide')?.hidden;
    this.sync();
    reconcileAutoplay();
  }

  /**
   * Clones the video out of its template. Idempotent.
   * @returns {HTMLVideoElement | null}
   */
  mount() {
    this.#dropStaleVideo();
    if (this.#video) return this.#video;

    const template = this.querySelector('template');
    if (!(template instanceof HTMLTemplateElement)) return null;

    // Cloning inside the template's inert document means nothing is fetched until the video is appended.
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const video = fragment.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return null;

    // The card's own poster image is already on screen, so skip the duplicate poster request.
    video.removeAttribute('poster');
    // Safari ignores the muted attribute on dynamically created videos, so set the properties too.
    video.muted = true;
    video.playsInline = true;
    video.addEventListener('playing', () => this.toggleAttribute('data-playing', true));

    this.append(video);
    this.setAttribute('data-video-mounted', '');
    this.#video = video;

    return video;
  }

  play() {
    if (this.mode === 'off' || !this.#isOnActiveSlide) return;

    const video = this.mount();
    if (!video) return;

    // Browsers reject play() when autoplay is blocked (e.g. iOS Low Power Mode). The poster stays in place.
    video.play().catch(() => {});
  }

  /**
   * Pauses and rewinds so the next play starts from the top.
   */
  pause() {
    this.removeAttribute('data-playing');

    const video = this.#video;
    if (!video) return;

    video.pause();
    if (video.currentTime > 0) video.currentTime = 0;
  }

  /**
   * @param {Event} event
   */
  #handleSlideshowSelect = (event) => {
    if (!(event instanceof SlideshowSelectEvent) || event.target !== this.#slideshow) return;

    const isOnActiveSlide = event.detail.slide?.contains(this) ?? false;
    if (isOnActiveSlide === this.#isOnActiveSlide) return;

    this.#isOnActiveSlide = isOnActiveSlide;

    if (!isOnActiveSlide) {
      autoplaying.delete(this);
      this.pause();
    }

    reconcileAutoplay();
  };
}

if (!customElements.get('card-video')) {
  customElements.define('card-video', CardVideo);
}
